import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

/*
 * Sign-in against the Cognito user pool directly, so the credential form can be
 * part of this app instead of the AWS-hosted page.
 *
 * SRP: the password is used to prove knowledge of itself and is never sent to
 * Cognito. That is why this needs the pool id as well as the client id — the
 * hosted-UI redirect only needed the client.
 *
 * Tokens are written to the same localStorage keys api/auth.js already reads,
 * so everything downstream of sign-in is unchanged.
 */

const config = window.pcaSettings;

export const isCustomAuthConfigured = () => Boolean(config?.auth?.userPoolId);

const getPool = () => {
  if (!isCustomAuthConfigured()) {
    throw new Error(
      'Sign-in is not configured: auth.userPoolId is missing from config.js.'
    );
  }
  return new CognitoUserPool({
    UserPoolId: config.auth.userPoolId,
    ClientId: config.auth.clientId,
  });
};

const getUser = (username) => new CognitoUser({ Username: username, Pool: getPool() });

const storeSession = (session) => {
  window.localStorage.setItem('id_token', session.getIdToken().getJwtToken());
  window.localStorage.setItem('access_token', session.getAccessToken().getJwtToken());
  window.localStorage.setItem('refresh_token', session.getRefreshToken().getToken());
};

/*
 * Cognito's own error messages are aimed at developers. These are the ones a
 * user can act on; anything unrecognised falls through unchanged rather than
 * being flattened into a generic failure that hides the real cause.
 */
const friendlyError = (err) => {
  switch (err?.code) {
    case 'NotAuthorizedException':
      // Cognito reuses this one code for several distinct conditions,
      // distinguished only by message text — collapsing them all into
      // "incorrect password" hid an active brute-force lockout as an
      // ordinary credential mismatch.
      if (/attempts exceeded/i.test(err.message)) {
        return 'Too many failed sign-in attempts. Wait a few minutes and try again.';
      }
      if (/disabled/i.test(err.message)) {
        return 'This account has been disabled. Contact your administrator.';
      }
      return 'Incorrect username or password.';
    case 'UserNotFoundException':
      return 'Incorrect username or password.';
    case 'PasswordResetRequiredException':
      return 'Your password must be reset before you can sign in.';
    case 'UserNotConfirmedException':
      return 'This account has not been confirmed yet. Contact your administrator.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Wait a moment and try again.';
    case 'CodeMismatchException':
      return 'That code is not correct.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one.';
    case 'InvalidPasswordException':
      return err.message?.replace(/^InvalidPasswordException: /, '') || 'That password does not meet the requirements.';
    default:
      return err?.message || 'Something went wrong. Try again.';
  }
};

// Cognito's own error carries the `code` callers need to branch on (e.g. to
// route a reset-required account into the forgot-password flow), but its
// `message` isn't fit for display — so the friendly text travels as
// `.message` and the original code travels alongside it as `.code`.
const codedError = (err) => {
  const wrapped = new Error(friendlyError(err));
  wrapped.code = err?.code;
  return wrapped;
};

/**
 * Resolves to one of:
 *   { status: 'SUCCESS' }
 *   { status: 'NEW_PASSWORD_REQUIRED', user, requiredAttributes }
 *   { status: 'MFA_REQUIRED', user, challengeName }
 * and rejects with a message suitable for display.
 */
export function signIn(username, password) {
  return new Promise((resolve, reject) => {
    let user;
    try {
      user = getUser(username);
    } catch (err) {
      reject(new Error(err.message));
      return;
    }

    user.authenticateUser(
      new AuthenticationDetails({ Username: username, Password: password }),
      {
        onSuccess: (session) => {
          storeSession(session);
          resolve({ status: 'SUCCESS' });
        },
        onFailure: (err) => reject(codedError(err)),
        // Admin-created accounts arrive with a temporary password, so this is a
        // normal first sign-in, not an error path.
        newPasswordRequired: (userAttributes) => {
          // Cognito rejects these back on the completion call.
          delete userAttributes.email_verified;
          delete userAttributes.phone_number_verified;
          resolve({ status: 'NEW_PASSWORD_REQUIRED', user, requiredAttributes: userAttributes });
        },
        mfaRequired: (challengeName) =>
          resolve({ status: 'MFA_REQUIRED', user, challengeName }),
        totpRequired: (challengeName) =>
          resolve({ status: 'MFA_REQUIRED', user, challengeName }),
      }
    );
  });
}

export function completeNewPassword(user, newPassword, requiredAttributes = {}) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, requiredAttributes, {
      onSuccess: (session) => {
        storeSession(session);
        resolve({ status: 'SUCCESS' });
      },
      onFailure: (err) => reject(new Error(friendlyError(err))),
      mfaRequired: (challengeName) =>
        resolve({ status: 'MFA_REQUIRED', user, challengeName }),
    });
  });
}

export function submitMfaCode(user, code, challengeName = 'SMS_MFA') {
  return new Promise((resolve, reject) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => {
          storeSession(session);
          resolve({ status: 'SUCCESS' });
        },
        onFailure: (err) => reject(new Error(friendlyError(err))),
      },
      challengeName
    );
  });
}

export function requestPasswordReset(username) {
  return new Promise((resolve, reject) => {
    getUser(username).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(friendlyError(err))),
      // Fires once the code is on its way; treat it the same as success.
      inputVerificationCode: () => resolve(),
    });
  });
}

export function confirmPasswordReset(username, code, newPassword) {
  return new Promise((resolve, reject) => {
    getUser(username).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(new Error(friendlyError(err))),
    });
  });
}
